import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { indexedDBManager } from '../services/IndexDBService';
import SentryService from '../services/SentryService';
import type { StorageService } from '../services/storage/StorageService';

interface StorageCleanupDialogProps {
  open: boolean;
  onClose: () => void;
  onCleanupComplete?: () => void;
  storageInfo?: {
    usagePercent: number;
    availableMB: number;
    quotaMB: number;
  };
  minimumRequiredQuotaMB?: number; // New prop to specify minimum required quota in MB
  storageService?: StorageService | null;
}

export const StorageCleanupDialog = ({
  open,
  onClose,
  onCleanupComplete,
  storageInfo: initialStorageInfo,
  minimumRequiredQuotaMB = 15, // Default to 15MB if not provided
  storageService,
}: StorageCleanupDialogProps) => {
  const [isClearing, setIsClearing] = useState(false);
  const [clearingComplete, setClearingComplete] = useState(false);
  const [storageInfo, setStorageInfo] = useState(initialStorageInfo);
  const [isInsufficientQuota, setIsInsufficientQuota] = useState(false);

  // Convert MB to bytes for calculations
  const minimumRequiredQuota = minimumRequiredQuotaMB * 1024 * 1024;

  useEffect(() => {
    const checkStorage = async () => {
      if (!initialStorageInfo && open) {
        try {
          const estimate = await navigator.storage.estimate();
          if (estimate.quota !== undefined && estimate.usage !== undefined) {
            const availableMB = Math.round((estimate.quota - estimate.usage) / (1024 * 1024));
            const quotaMB = Math.round(estimate.quota / (1024 * 1024));
            const usagePercent = Math.round((estimate.usage / estimate.quota) * 100);

            setIsInsufficientQuota(estimate.quota < minimumRequiredQuota);

            setStorageInfo({
              availableMB,
              quotaMB,
              usagePercent,
            });
          }
        } catch (error) {
          console.error('Error checking storage:', error);
        }
      } else if (initialStorageInfo) {
        const providedQuotaInBytes = initialStorageInfo.quotaMB * 1024 * 1024;
        setIsInsufficientQuota(providedQuotaInBytes < minimumRequiredQuota);
        setStorageInfo(initialStorageInfo);
      }
    };

    checkStorage();
  }, [initialStorageInfo, open, minimumRequiredQuota]);

  const handleClearStorage = async () => {
    setIsClearing(true);
    try {
      if (storageService) {
        await storageService.clearAllSessions();
      } else {
        await indexedDBManager.clearAll();
      }

      SentryService.addBreadcrumb({
        category: 'storage',
        message: `User manually cleared storage via ${storageService ? 'StorageService' : 'indexedDBManager'}`,
        level: 'info',
      });

      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        if (estimate.quota && estimate.usage) {
          const availableMB = Math.round((estimate.quota - estimate.usage) / (1024 * 1024));
          const quotaMB = Math.round(estimate.quota / (1024 * 1024));
          const usagePercent = Math.round((estimate.usage / estimate.quota) * 100);

          setStorageInfo({
            availableMB,
            quotaMB,
            usagePercent,
          });
        }
      }

      setClearingComplete(true);
      setTimeout(() => {
        if (onCleanupComplete) {
          onCleanupComplete();
        }
        setIsClearing(false);
        setClearingComplete(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error clearing IndexedDB storage:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'StorageCleanupDialog' },
        extra: { method: 'handleClearStorage' },
      });
      setIsClearing(false);
    }
  };

  const getBrowserInstructions = () => {
    const browserType = navigator.userAgent.includes('Chrome')
      ? 'Chrome'
      : navigator.userAgent.includes('Firefox')
        ? 'Firefox'
        : navigator.userAgent.includes('Safari')
          ? 'Safari'
          : 'Other';

    switch (browserType) {
      case 'Chrome':
        return (
          <Typography variant="body2" color="text.secondary">
            <ul>
              <li>Go to chrome://settings/content/siteData</li>
              <li>Make sure third-party cookies are allowed</li>
              <li>Check for any site-specific storage restrictions</li>
              <li>Make sure "Clear cookies and site data when you quit Chrome" is turned off</li>
            </ul>
          </Typography>
        );
      case 'Firefox':
        return (
          <Typography variant="body2" color="text.secondary">
            <ul>
              <li>Go to about:config (type in address bar)</li>
              <li>Search for "dom.indexedDB.enabled" and make sure it's set to true</li>
              <li>Search for "dom.storage.default_quota" and ensure it's not set too low</li>
              <li>
                Search for "privacy.trackingprotection" settings that might be blocking storage
              </li>
            </ul>
          </Typography>
        );
      case 'Safari':
        return (
          <Typography variant="body2" color="text.secondary">
            <ul>
              <li>Safari has stricter storage limitations - try using Chrome if possible</li>
              <li>
                Go to Safari Preferences → Privacy → Website tracking and ensure "Prevent cross-site
                tracking" is off
              </li>
              <li>
                In Safari Preferences → Privacy, make sure "Block all cookies" is not selected
              </li>
            </ul>
          </Typography>
        );
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            <ul>
              <li>Restart your browser</li>
              <li>Ensure your device has at least 1 GB of free disk space</li>
              <li>Update your browser to the latest version</li>
              <li>Disable browser extensions that might limit website capabilities</li>
            </ul>
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={isClearing ? undefined : onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>
        {isInsufficientQuota ? 'Storage Quota Too Low' : 'Clear Browser Storage'}
      </DialogTitle>
      <DialogContent>
        {storageInfo && !isInsufficientQuota && (
          <Box sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ position: "relative", display: "inline-flex", mb: 1 }}>
              <CircularProgress
                variant="determinate"
                value={storageInfo.usagePercent}
                size={80}
                thickness={4}
                color={storageInfo.usagePercent > 90 || isInsufficientQuota ? 'error' : 'primary'}
              />
              <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Typography variant="caption" component="div" color="textSecondary">
                  {`${Math.round(storageInfo.usagePercent)}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary" align="center">
              {`${storageInfo.availableMB} MB free of ${storageInfo.quotaMB} MB total`}
            </Typography>
          </Box>
        )}

        {isInsufficientQuota && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your browser is only allowing {storageInfo?.quotaMB || 0} MB of storage for this site,
            which is not enough for recording. You need at least{' '}
            {Math.ceil(minimumRequiredQuota / (1024 * 1024))} MB.
          </Alert>
        )}

        <DialogContentText>
          {isClearing ? (
            clearingComplete ? (
              'Storage successfully cleared! You should now have enough space to record.'
            ) : (
              'Clearing storage...'
            )
          ) : isInsufficientQuota ? (
            <>
              Your browser has allocated too little storage quota for this application to function
              properly. This can happen in private/incognito browsing modes, due to browser
              settings, or on devices with very limited storage.
              <br />
              <br />
              Try the following solutions:
              <ul>
                <li>Use a regular (non-private/non-incognito) browsing window</li>
                <li>Try using a different browser (Chrome typically provides more storage)</li>
                <li>Free up more disk space on your device</li>
                <li>Check if there are any browser storage restrictions in your settings</li>
              </ul>
            </>
          ) : (
            <>
              You don't have enough browser storage space available to record.
              <br />
              <br />
              This can happen if you've recorded multiple sessions without submitting them, or if
              other websites are using a lot of storage.
              <br />
              <br />
              Clicking "Clear Storage" below will remove any unsaved audio data from previous
              recording sessions in this app, but will allow you to start a new recording.
            </>
          )}
        </DialogContentText>

        {!isClearing && !isInsufficientQuota && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Having trouble? Clear storage from other websites</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
                If clearing this app's storage doesn't solve the problem, other websites might be
                using significant storage space. For security reasons, we can't clear data from
                other websites, but you can do this through your browser settings.
              </Typography>
              {getBrowserInstructions()}
            </AccordionDetails>
          </Accordion>
        )}

        {!isClearing && isInsufficientQuota && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Advanced storage troubleshooting</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }} component="p">
                Browser-specific recommendations:
              </Typography>
              {getBrowserInstructions()}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
      <DialogActions>
        {isClearing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {!clearingComplete && <CircularProgress size={24} sx={{ mr: 1 }} />}
            <Typography variant="body2">
              {clearingComplete ? 'Complete!' : 'Clearing storage...'}
            </Typography>
          </Box>
        ) : (
          <>
            <Button onClick={onClose} color="primary">
              Close
            </Button>
            {!isInsufficientQuota && (
              <Button onClick={handleClearStorage} color="error">
                Clear Storage
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
